import axios, {
  AxiosInstance,
  AxiosError,
  AxiosResponse,
  AxiosHeaders,
  InternalAxiosRequestConfig,
} from "axios";

import Cookies from "universal-cookie";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logoutUser } from "../../redux/reducers/userDataSlice";
import { sendLogToBackend, handleUnauthorized, shouldSuppressUnauthorizedLogout } from "../../shared-lib/error-logger/global-error-logger";
import { clearAllStorage } from "../utils/clear-all-storage";
import { refreshAccessToken } from "./token-refresh";
import { getGuestHomePath } from "../utils/home-path";

type RequestConfigWithRefreshRetry = InternalAxiosRequestConfig & {
  _retryAfterRefresh?: boolean;
};

function buildErrorLog(error: AxiosError) {
  return {
    name: error.name || "API Error",
    url: error.config?.url || "Unknown URL",
    status: error.response?.status?.toString() || "Unknown Status",
    statusText: error.response?.statusText || "Unknown Status Text",
    message: error.message || "No error message",
    jsonMessage: error.response?.data || {},
    httpMethod: error.config?.method?.toUpperCase() || "UNKNOWN",
    requestPayload: error.config?.data || {},
  };
}

function createApiInstance(baseURL: string): AxiosInstance {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    (config) => {
      const accessToken = localStorage.getItem("AccessToken");
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      if (!error.config) return Promise.reject(error);
      const originalRequest = error.config as RequestConfigWithRefreshRetry;

      if (error.response?.status === 401) {
        if (shouldSuppressUnauthorizedLogout()) {
          return Promise.reject(error);
        }

        const requestUrl = originalRequest.url || "";
        // Google Calendar API failures should not force a global logout
        if (
          requestUrl.includes("/api/GoogleCalendar") ||
          requestUrl.includes("/api/google/")
        ) {
          return Promise.reject(error);
        }
        const isRefreshCall = requestUrl.includes("RefreshToken");

        if (isRefreshCall) {
          const errorLog = buildErrorLog(error);
          await sendLogToBackend(errorLog);
          handleUnauthorized();
          
          return Promise.reject(error);
        }

        if (originalRequest._retryAfterRefresh) {
          const errorLog = buildErrorLog(error);
          await sendLogToBackend(errorLog);
          handleUnauthorized();
          return Promise.reject(error);
        }

        const accessToken = localStorage.getItem("AccessToken");
        const refreshToken = localStorage.getItem("refreshToken");
        if (!accessToken || !refreshToken) {
          const errorLog = buildErrorLog(error);
          await sendLogToBackend(errorLog);
          handleUnauthorized();
          return Promise.reject(error);
        }

        const refreshResult = await refreshAccessToken();
        if (refreshResult.success === false) {
          const errorLog = buildErrorLog(error);
          await sendLogToBackend(errorLog);
          if (refreshResult.shouldLogout) {
            handleUnauthorized();
          }
          return Promise.reject(error);
        }

        originalRequest._retryAfterRefresh = true;
        const headers = AxiosHeaders.from(originalRequest.headers ?? {});
        headers.set("Authorization", `Bearer ${refreshResult.accessToken}`);
        originalRequest.headers = headers;
        return instance.request(originalRequest);
      }

      const errorLog = buildErrorLog(error);
      await sendLogToBackend(errorLog);
      return Promise.reject(error);
    }
  );

  return instance;
}

const ClearCookie = async () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Clear ALL storage types (localStorage, sessionStorage, cookies, Cache API, IndexedDB, etc.)
  await clearAllStorage();
  
  dispatch(logoutUser());
  navigate(getGuestHomePath(), { state: { type: "back" } });
};

const apiUrlInstance = process.env.REACT_APP_URL;
const apiUrlvirtcochatbook = process.env.REACT_APP_EVO_CHAT_API_URL;
const apiUrlvirtcochathub = process.env.REACT_APP_EVO_CHAT_HUB_URL;
const apiUrlVirtcoStcInstance = process.env.REACT_APP_VIRTCO_STS_URL;
// const apiUrlVirtcoGateway = process.env.REACT_APP_URL;
const apiUrlVirtcoOrder = process.env.REACT_APP_VIRTCO_ORDER;

const apiInstance1 = createApiInstance(apiUrlInstance);
const apichatbookInstance = createApiInstance(apiUrlvirtcochatbook);
const apiInstancevirtcochathub = createApiInstance(apiUrlvirtcochathub);
const apiInstanceVirtcoStc = createApiInstance(apiUrlVirtcoStcInstance);
const apiInstanceVirtcogateway = createApiInstance(apiUrlInstance);
const apiInstanceVirtcoOrder = createApiInstance(apiUrlVirtcoOrder);

export {
  apiInstance1,
  apichatbookInstance,
  apiInstancevirtcochathub,
  apiInstanceVirtcoStc,
  apiInstanceVirtcogateway,
  apiInstanceVirtcoOrder
};
